import { Helmet } from 'react-helmet';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Plus, Minus, ArrowRight } from 'lucide-react';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQCategory {
  title: string;
  items: FAQItem[];
}

const FAQ = () => {
  const [activeItems, setActiveItems] = useState<Set<string>>(new Set());

  const toggleItem = (itemId: string) => {
    setActiveItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const faqCategories: FAQCategory[] = [
    {
      title: "General Questions",
      items: [
        {
          question: "What is InvizHer?",
          answer: "InvizHer is a personal blog focused on sharing programming knowledge, development resources, and coding tutorials. We aim to make technical content accessible to everyone interested in learning to code."
        },
        {
          question: "Is the content free?",
          answer: "Yes! All our content is completely free and accessible to everyone. We believe in making programming education available to all interested learners."
        }
      ]
    },
    {
      title: "Content & Resources",
      items: [
        {
          question: "How often is content updated?",
          answer: "We publish new articles and tutorials regularly, typically 2-3 times per week. The topics cover various programming languages, development tools, and industry best practices."
        },
        {
          question: "Can I request specific topics?",
          answer: "Absolutely! You can suggest topics through our contact page. We value community input and try to create content based on reader requests."
        }
      ]
    },
    {
      title: "Technical Support",
      items: [
        {
          question: "How do I report issues?",
          answer: "If you find any issues with the content or website, please use our contact form or reach out through GitHub. We appreciate your feedback and work quickly to address any problems."
        },
        {
          question: "Can I contribute content?",
          answer: "While we currently don't accept direct content submissions, you can suggest topics or corrections through our GitHub repository or contact form."
        }
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>FAQ | Programming Help & Resources | InvizHer</title>
        <meta name="description" content="Find answers about InvizHer's programming tutorials, development resources, coding projects, and learning materials. Get help with web development, programming languages, and technical concepts." />
        <meta name="keywords" content="programming FAQ, coding help, development resources, programming tutorials, coding projects, web development help, InvizHer programming, learning to code, programming education" />
        <link rel="canonical" href="https://invizher.com/faq" />
        
        {/* JSON-LD Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is InvizHer?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "InvizHer is a personal blog focused on sharing programming knowledge, development resources, and coding tutorials. We aim to make technical content accessible to everyone interested in learning to code."
                }
              },
              {
                "@type": "Question",
                "name": "Is the content free?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes! All our content is completely free and accessible to everyone. We believe in making programming education available to all interested learners."
                }
              },
              {
                "@type": "Question",
                "name": "How often is content updated?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "We publish new articles and tutorials regularly, typically 2-3 times per week. The topics cover various programming languages, development tools, and industry best practices."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-28" role="main">
          {/* Hero Section */}
          <section className="container mx-auto px-4 py-10">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-3xl"></div>
              <div className="relative glass-morphism rounded-3xl p-8 lg:p-16">
                <div className="text-center">
                  <h1 className="text-3xl lg:text-5xl font-bold mb-6">
                    <span className="gradient-text">Frequently Asked</span> Questions
                  </h1>
                  <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
                    Find answers to common questions about our programming resources, tutorials, and content sharing platform.
                  </p>
                  <Link 
                    to="/contact" 
                    className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-full hover:opacity-90 transition-opacity"
                  >
                    <span>Still have questions?</span>
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ Categories */}
          <section className="container mx-auto px-4 py-16">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {faqCategories.map((category, categoryIndex) => (
                <div key={categoryIndex} className="glass-morphism rounded-2xl p-6 lg:p-8">
                  <h2 className="text-xl font-bold mb-6 gradient-text">{category.title}</h2>
                  <div className="space-y-4">
                    {category.items.map((item, itemIndex) => {
                      const itemId = `${categoryIndex}-${itemIndex}`;
                      const isActive = activeItems.has(itemId);
                      
                      return (
                        <div 
                          key={itemIndex} 
                          className="cursor-pointer transition-all"
                          onClick={() => toggleItem(itemId)}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <h3 className="font-medium text-sm lg:text-base flex-1">{item.question}</h3>
                            <button className="flex-shrink-0 text-blue-500">
                              {isActive ? (
                                <Minus className="w-5 h-5" />
                              ) : (
                                <Plus className="w-5 h-5" />
                              )}
                            </button>
                          </div>
                          {isActive && (
                            <div className="mt-3 text-muted-foreground text-sm animate-in fade-in slide-in-from-top-2 duration-200">
                              {item.answer}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Section */}
          <section className="container mx-auto px-4 py-16">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-4">Still Have Questions?</h2>
              <p className="text-muted-foreground mb-8">
                Can't find what you're looking for? Feel free to reach out to us directly.
              </p>
              <Link 
                to="/contact" 
                className="inline-block px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-full hover:opacity-90 transition-opacity"
              >
                Contact Us
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default FAQ;
